import React, { useEffect, useState } from 'react';
import { SystemAdminDataService } from '../../services/systemAdminDataService';
import { ERROR_STATUS_LABELS, ErrorStatus } from '../../services/systemAdminErrorsService';

/** Shared bits of the System Admin → Errors pages. */

const STATUS_STYLE: Record<ErrorStatus, { cls: string; icon: string }> = {
  new:           { cls: 'sys-state--over', icon: 'fa-circle-exclamation' },
  investigating: { cls: 'sys-state--warn', icon: 'fa-magnifying-glass' },
  resolved:      { cls: 'sys-state--on',   icon: 'fa-check-circle' },
  ignored:       { cls: 'sys-state--off',  icon: 'fa-eye-slash' },
};

export const ErrorStatusBadge: React.FC<{ status: ErrorStatus }> = ({ status }) => {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.new;
  return (
    <span className={`sys-state ${s.cls}`}>
      <i className={`fas ${s.icon}`} aria-hidden="true" />{ERROR_STATUS_LABELS[status] ?? status}
    </span>
  );
};

/** "12 Oct 2026, 14:03:27" — UK local time, seconds included (logs are per second). */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** "5 min ago", "3 h ago", "2 d ago". */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

/**
 * Organisation id → name, for labelling. Names are shown on screen only; the
 * error records themselves hold ids. A failed load just leaves ids showing.
 */
export function useOrgNames(): Map<number, string> {
  const [names, setNames] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    let cancelled = false;
    SystemAdminDataService.fetchOrganisations()
      .then(orgs => { if (!cancelled) setNames(new Map(orgs.map(o => [o.id, o.name]))); })
      .catch(() => { /* ids only */ });
    return () => { cancelled = true; };
  }, []);
  return names;
}

export function orgLabel(names: Map<number, string>, id: number | null | undefined): string {
  if (id === null || id === undefined) return '—';
  return names.get(id) ?? `#${id}`;
}

/** A request id in monospace with a copy button. */
export const CopyableId: React.FC<{ value: string | null | undefined; title?: string }> = ({ value, title }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="sys-table__muted">—</span>;
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the id is still selectable */ }
  };
  return (
    <span className="sys-errors__id" title={title}>
      <code>{value}</code>
      <button type="button" className="sys-errors__copy" onClick={copy} aria-label="Copy" title={copied ? 'Copied' : 'Copy'}>
        <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true" />
      </button>
    </span>
  );
};

/** Opens CloudWatch Logs Insights (via the AWS sign-in shortcut) in a new tab. */
export const LogsLink: React.FC<{ href: string | null | undefined; label?: string }> = ({ href, label = 'CloudWatch logs' }) =>
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
       title="Logs Insights, filtered to this request — sign in to AWS if asked">
      <i className="fas fa-up-right-from-square me-1" aria-hidden="true" />{label}
    </a>
  ) : (
    <span className="sys-table__muted" title="Not configured on the API (CLOUDWATCH_LOG_GROUPS / AWS_* settings)">—</span>
  );
